import * as React from "react";
import { motion, AnimatePresence, PanInfo, useDragControls } from "framer-motion";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  snapPoints?: number[];
  className?: string;
}

export function BottomSheet({
  open,
  onOpenChange,
  children,
  title,
  snapPoints = [0.5, 0.9],
  className,
}: BottomSheetProps) {
  const prefersReducedMotion = useReducedMotion();
  const [currentSnap, setCurrentSnap] = React.useState(0);
  const dragControls = useDragControls();
  const sheetRef = React.useRef<HTMLDivElement>(null);

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const velocity = info.velocity.y;
    const offset = info.offset.y;

    // Fast swipe down = close
    if (velocity > 500 || offset > 150) {
      onOpenChange(false);
      return;
    }

    // Fast swipe up = expand
    if (velocity < -500 || offset < -100) {
      setCurrentSnap(Math.min(currentSnap + 1, snapPoints.length - 1));
      return;
    }
  };

  const currentHeight = `${snapPoints[currentSnap] * 100}vh`;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => onOpenChange(false)}
          />

          {/* Sheet */}
          <motion.div
            ref={sheetRef}
            initial={{ y: "100%" }}
            animate={{ y: 0, height: currentHeight }}
            exit={{ y: "100%" }}
            transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", damping: 30, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.1, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            className={cn(
              "fixed bottom-0 left-0 right-0 z-50",
              "bg-background rounded-t-3xl shadow-2xl",
              "flex flex-col",
              "safe-area-inset-bottom",
              className
            )}
          >
            {/* Drag handle */}
            <div
              className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-12 h-1.5 rounded-full bg-muted-foreground/30" />
            </div>

            {/* Header */}
            {title && (
              <div className="flex items-center justify-between px-6 pb-4 border-b border-border">
                <h2 className="text-lg font-semibold">{title}</h2>
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors touch-manipulation"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Simple bottom drawer variant
interface BottomDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
}

export function BottomDrawer({
  open,
  onOpenChange,
  children,
  title,
  className,
}: BottomDrawerProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="fixed inset-0 z-[60] bg-black"
            onClick={() => onOpenChange(false)}
          />

          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", damping: 30, stiffness: 300 }}
            className={cn(
              "fixed inset-0 z-[60]",
              "bg-background",
              className
            )}
          >
            {title && (
              <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 py-4 bg-background/80 backdrop-blur-sm border-b border-border">
                <h2 className="text-lg font-semibold">{title}</h2>
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors touch-manipulation"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className="w-full h-full">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
